import Vue from "vue"
import moment from "moment"
import { RUNE_PAGE_ICON } from "../../constants/league_of_legends"
import { flow, flatten, map, groupBy, toPairs } from "lodash/fp"
import { set, get, keyBy, head } from "lodash"
import {
  fetchSummonerSpells,
  fetchItems,
  fetchRunes,
  fetchServerVersion,
  fetchChampions,
  fetch5v5TierList,
  fetchGuidesByChampionId,
  getUserLeagueInfo, getUserLeagueRank, getUserLeagueMatches, hardRefresh, getSummonerSpells, getRunes, getMatchByServerAndId, getSingleMatchHistory,
} from "../../api/leagueoflegends"
import championDefaultImage from "@/assets/images/leagueoflegends/champions/champion-default.png"
const state = () => ({
  patches: [],
  champions: {},
  champion5v5Data: {},
  tierList: [],
  tierListRaw: {},
  championRoles: {},
  summonerSpells: {},
  items: {},
  runes: {},
  serverVersion: "",
  championGuides: {},
  networkError: null,
  // added for dashboard
  leagueInfo: null,
  leagueRank: null,
  leagueMatches: [],
  availableChampions: {},
  loadedAll: false,
  summonerSpellsObj: null,
  runesObj: null,
  refreshError: "",
  loadingMoreMatch: false,
  matchesInLoading: [],
  showRankCheck: false,
  showRankBtnBlue: false,
  loadingWidth: 100,
})

const getters = {
  isUserPro: (state, getters, rootState) => {
    const leagueCapabilities = get(rootState, "User.userCapabilities.pro_content.leagueoflegends")
    return !!leagueCapabilities && leagueCapabilities.includes("view")
  },
  getLatestPatch: (state) => {
    return state.patches[0]
  },
  getLatestPatchDate: (state) => {
    const patchReleaseDate = get(head(state.patches), "date_released.date")
    return patchReleaseDate
      ? moment.utc(patchReleaseDate).format("MMM D, YYYY")
      : null
  },
  getChampionThumbnailUrl: (state) => (championId) => {
    const thumbnail = get(state, `champions[${championId}].thumbnail_image`)
    return thumbnail || championDefaultImage
  },
  getChampions: (state) => {
    return state.champions
  },
  getChampion: (state) => (championId) => {
    return get(state, `champions[${championId}]`)
  },
  searchChampions: (state) => (searchQuery) => {
    const lowerCaseQuery = searchQuery.toLowerCase()
    return state.champions.filter(champion => {
      return champion.name.toLowerCase().indexOf(lowerCaseQuery) > -1
    })
  },
  getChampion5v5Data: (state) => (championId) => {
    return get(state, `champion5v5Data[${championId}]`)
  },
  getServerVersion: (state) => {
    return state.serverVersion
  },
  // Summoner spells
  getSummonerSpells: (state) => {
    return state.summonerSpells
  },
  getSummonerSpell: (state) => (id) => {
    return get(state, `summonerSpells[${id}]`)
  },
  // Items
  getItems: (state) => {
    return state.items
  },
  getItem: (state) => (id) => {
    return get(state, `items[${id}]`)
  },
  // Runes
  getRunes: (state) => {
    return state.runes
  },
  getRune: (state) => (id) => {
    return get(state, `runes[${id}]`)
  },
  currentSeason: () => 10,
  // Added for dashboard
  leagueInfo: state => state.leagueInfo,
  leagueRank: state => state.leagueRank,
  leagueMatches: state => state.leagueMatches,
  availableChampions: state => state.availableChampions,
  loadedAll: state => state.loadedAll,
  refreshError: state => state.refreshError,
  matchesInLoading: state => state.matchesInLoading,
  showRankCheck: state => state.showRankCheck,
  showRankBtnBlue: state => state.showRankBtnBlue,
  loadingWidth: state => state.loadingWidth,
}

const mutations = {
  "UPDATE_LEAGUE_OF_LEGENDS_PATCHES": (state, payload) => {
    Vue.set(state, "patches", payload)
  },
  "UPDATE_LEAGUE_OF_LEGENDS_TIER_LIST": (state, { tierList, championRoles }) => {
    Vue.set(state, "tierList", tierList)
    Vue.set(state, "championRoles", championRoles)
  },
  "UPDATE_LEAGUE_OF_LEGENDS_CHAMPIONS": (state, payload) => {
    Vue.set(state, "champions", payload)
  },
  "UPDATE_LEAGUE_OF_LEGENDS_CHAMPION": (state, payload) => {
    Vue.set(state.champions, payload.id, payload)
  },
  "UPDATE_LEAGUE_OF_LEGENDS_CHAMPION_5V5_DATA": (state, payload) => {
    Vue.set(state.champion5v5Data, payload.champion_id, payload)
  },
  "UPDATE_LEAGUE_OF_LEGENDS_SUMMONER_SPELLS": (state, payload) => {
    Vue.set(state, "summonerSpells", payload)
  },
  "UPDATE_LEAGUE_OF_LEGENDS_ITEMS": (state, payload) => {
    Vue.set(state, "items", payload)
  },
  "UPDATE_LEAGUE_OF_LEGENDS_RUNES": (state, payload) => {
    Vue.set(state, "runes", payload)
  },
  "UPDATE_LEAGUE_OF_LEGENDS_SERVER_VERSION": (state, payload) => {
    Vue.set(state, "serverVersion", payload)
  },
  "UPDATE_LEAGUE_OF_LEGENDS_CHAMPION_GUIDES": (state, { championId, guides }) => {
    Vue.set(state, `championGuides[${championId}]`, guides)
  },
  "UPDATE_NETWORK_ERROR": (state, payload) => {
    Vue.set(state, "networkError", payload)
  },
  // added for dashboard
  "setLeagueInfo": (state, info) => (state.leagueInfo = info),
  "setLeagueRank": (state, rankInfo) => (state.leagueRank = rankInfo),
  "setLeagueMatches": (state, leagueMatches) => (state.leagueMatches = leagueMatches),
  "setAvailableChampions": (state, availableChampions) => (state.availableChampions = availableChampions),
  "setLoadedAll": (state, bol) => (state.loadedAll = bol),

  "setSummonerSpells": (state, summonerSpells) => (state.summonerSpellsObj = summonerSpells),
  "setRunes": (state, runes) => (state.runesObj = runes),
  "setRefreshError": (state, refreshError) => (state.refreshError = refreshError),
  "setLoadingMoreMatch": (state, bol) => (state.loadingMoreMatch = bol),
  "setMatchesInLoading": (state, matches) => (state.matchesInLoading = matches),
  "setShowRankCheck": (state, showRankCheck) => (state.showRankCheck = showRankCheck),
  "setShowRankBtnBlue": (state, showRankBtnBlue) => (state.showRankBtnBlue = showRankBtnBlue),
  "setLoadingWidth": (state, loadingWidth) => (state.loadingWidth = loadingWidth),
}

function tierComparator ({ tier: tierA }, { tier: tierB }) {
  const tierValues = {
    s: 5,
    a: 4,
    b: 3,
    c: 2,
    d: 1,
  }

  return tierValues[tierB.toLowerCase()] - tierValues[tierA.toLowerCase()]
}

const actions = {
  updatePatches ({ commit }, payload) {
    commit("UPDATE_LEAGUE_OF_LEGENDS_PATCHES", payload)
  },
  updateTierList ({ commit }, payload) {
    Vue.set(state, "tierListByRole", groupBy("role")(Object.values(payload)))

    const championRoles = flow(
      map(({ tiers, role }) => {
        return map(({ champions, tier }) => {
          return map(champion => set(set(champion, "tier", tier), "role", role))(champions)
        })(tiers)
      }),
      flatten,
      flatten,
      groupBy("id"),
      map((roles) => {
        const id = roles[0].id

        roles.sort(tierComparator)

        return {
          id: id,
          primaryTier: roles[0].tier,
          primaryRole: roles[0].role,
          roles,
        }
      })
    )(payload)

    let tierList = flow(
      groupBy("primaryTier"),
      toPairs,
      map(([tier, champions]) => ({ tier, champions }))
    )(championRoles)
    tierList.sort(tierComparator)

    commit("UPDATE_LEAGUE_OF_LEGENDS_TIER_LIST", { tierList, championRoles: keyBy(championRoles, ({ id }) => id) })
  },
  updateChampions ({ commit }, payload) {
    commit("UPDATE_LEAGUE_OF_LEGENDS_CHAMPIONS", payload)
  },
  updateChampion ({ commit }, payload) {
    commit("UPDATE_LEAGUE_OF_LEGENDS_CHAMPION", payload)
  },
  updateChampion5v5Data ({ commit }, payload) {
    commit("UPDATE_LEAGUE_OF_LEGENDS_CHAMPION_5V5_DATA", payload)
  },
  // Data bootstrapping
  loadStaticData ({ dispatch }) {
    dispatch("loadTierList")
    dispatch("loadChampions")
    dispatch("loadSummonerSpells")
    dispatch("loadItems")
    dispatch("loadRunes")
    dispatch("loadServerVersion")
  },
  loadSummonerSpells ({ commit }) {
    fetchSummonerSpells()
      .then(spells => commit("UPDATE_LEAGUE_OF_LEGENDS_SUMMONER_SPELLS", spells))
      .catch(err => {
        console.error(err)
        commit("UPDATE_NETWORK_ERROR", err)
      })
  },
  loadItems ({ commit }) {
    fetchItems()
      .then(items => commit("UPDATE_LEAGUE_OF_LEGENDS_ITEMS", items))
      .catch(err => {
        console.error(err)
        commit("UPDATE_NETWORK_ERROR", err)
      })
  },
  loadRunes ({ commit }) {
    fetchRunes()
      .then(runes => commit("UPDATE_LEAGUE_OF_LEGENDS_RUNES", runes))
      .catch(err => {
        console.error(err)
        commit("UPDATE_NETWORK_ERROR", err)
      })
  },
  loadServerVersion ({ commit }) {
    fetchServerVersion()
      .then(version => commit("UPDATE_LEAGUE_OF_LEGENDS_SERVER_VERSION", version))
      .catch(err => {
        console.error(err)
        commit("UPDATE_NETWORK_ERROR", err)
      })
  },
  loadChampions ({ commit }) {
    return fetchChampions()
      .then(champions => commit("UPDATE_LEAGUE_OF_LEGENDS_CHAMPIONS", champions))
      .catch(err => {
        console.error(err)
        commit("UPDATE_NETWORK_ERROR", err)
      })
  },
  loadTierList ({ dispatch, commit }) {
    fetch5v5TierList()
      .then(tierList => dispatch("updateTierList", tierList))
      .catch(err => {
        console.error(err)
        commit("UPDATE_NETWORK_ERROR", err)
      })
  },
  loadChampionGuides ({ commit, state, rootState }, championId) {
    fetchGuidesByChampionId(championId)
      .then(guides => commit("UPDATE_LEAGUE_OF_LEGENDS_CHAMPION_GUIDES", { championId, guides }))
      .catch(err => {
        console.error(err)
        commit("UPDATE_NETWORK_ERROR", err)
      })
  },
  // for dashboard
  async retrieveLeagueInfo ({ commit }) {
    const data = await getUserLeagueInfo()
    commit("setLeagueInfo", data)
  },
  async retrieveLeagueRank ({ commit }) {
    const data = await getUserLeagueRank()
    commit("setLeagueRank", data)
  },
  async retrieveLeagueMatches ({ commit, state, dispatch }) {
    if (!state.summonerSpellsObj) {
      await dispatch("getSummonerSpells")
    }
    if (!state.runesObj) {
      await dispatch("getRunes")
    }
    const data = await getUserLeagueMatches(state.leagueMatches.length)
    const matches = await dispatch("mapMatches", data.matches)
    await commit("setLeagueMatches", matches)
    for (let match of state.leagueMatches) {
      if (!match.performance) {
        commit("setMatchesInLoading", [...state.matchesInLoading, match.id])
        dispatch("updateMatch", {
          server: match.server,
          id: match.id,
        })
      }
    }
  },
  async fetchChampions ({ commit }) {
    const champions = await fetchChampions()
    commit("setAvailableChampions", champions)
  },
  async loadMoreMatches ({ commit, state, dispatch }) {
    if (state.loadingMoreMatch) {
      return
    }
    await commit("setLoadingMoreMatch", true)
    const data = await getUserLeagueMatches(state.leagueMatches.length)
    if (data.matches.length < 6) {
      commit("setLoadedAll", true)
    }
    const matches = await dispatch("mapMatches", data.matches)
    await commit("setLeagueMatches", [...state.leagueMatches, ...matches])
    commit("setLoadingMoreMatch", false)
  },
  async hardRefresh ({ commit, dispatch, state }) {
    commit("setLoadingWidth", 0)
    const increaseWidth = setInterval(() => {
      if (state.loadingWidth < 90) {
        commit("setLoadingWidth", state.loadingWidth + 10)
      }
    }, 100)

    commit("setLoadedAll", false)
    commit("setLeagueMatches", [])
    dispatch("retrieveLeagueInfo")
    const data = await hardRefresh()

    await dispatch("retrieveLeagueRank")

    if (state.leagueRank) {
      await dispatch("retrieveLeagueMatches")
      if (data.message) {
        commit("setRefreshError", data.response.data.message)
        setTimeout(() => {
          commit("setRefreshError", "")
        }, 5000)
      } else {
        await commit("setShowRankCheck", true)
        setTimeout(() => {
          commit("setShowRankBtnBlue", true)
        }, 1000)
        setTimeout(() => {
          commit("setShowRankCheck", false)
        }, 2000)
        setTimeout(() => {
          commit("setShowRankBtnBlue", false)
        }, 60000)
      }
    }

    clearInterval(increaseWidth)
    commit("setLoadingWidth", 100)
  },
  async getSummonerSpells ({ commit }) {
    const data = await getSummonerSpells()
    const summonerSpells = {}
    for (let spell of Object.values(data)) {
      summonerSpells[spell.id] = spell
    }
    commit("setSummonerSpells", summonerSpells)
  },
  async getRunes ({ commit }) {
    const data = await getRunes()
    const runes = {}
    for (let rune of Object.values(data)) {
      runes[rune.id] = rune
    }
    commit("setRunes", runes)
  },
  mapMatches ({ state }, matches) {
    return matches.map((match) => {
      if (match.summoner_spells) {
        match.summoner_spells = match.summoner_spells.map(spellId => {
          return state.summonerSpellsObj[spellId]
        })
      }
      if (match.runes) {
        const rune1 = state.runesObj[match.runes.primary[0]]
        const rune2 = state.runesObj[match.runes.secondary_path]
        rune2.thumbnail_image = RUNE_PAGE_ICON[rune2.name.toLowerCase()]
        match.runes = {
          rune1,
          rune2,
        }
      }
      return match
    }
    )
  },
  async updateMatch ({ commit, state, dispatch }, { server, id }) {
    await getMatchByServerAndId(server, id)
    const matchIndex = state.leagueMatches.findIndex(
      match => (match.id === id)
    )
    const data = await getSingleMatchHistory(matchIndex)
    if (!data.matches.length) {
      return
    }
    const updatedMatches = await dispatch("mapMatches", data.matches)
    const matches = state.leagueMatches
    matches[matchIndex] = updatedMatches[0]
    await commit("setLeagueMatches", [...matches])
    const matchesInLoading = state.matchesInLoading
    const index = matchesInLoading.indexOf(id)
    if (index >= 0) {
      matchesInLoading.splice(index, 1)
      commit("setMatchesInLoading", matchesInLoading)
    }
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/leagueoflegends.js